import { useSelector } from 'react-redux';
import { getImpliedRules } from '../services/permissionRules';

/**
 * usePermissions Hook
 *
 * Custom hook to manage user permissions and role-based access control.
 * Reads permission data from Redux auth state and provides helper functions.
 *
 * @returns {Object} Permission checking functions and user permission data
 *
 * @example
 * const { hasPermission, hasAnyPermission, hasAllPermissions, isTenantAdmin } = usePermissions();
 *
 * if (hasPermission('crm.add_sales_order')) {
 *   // Show create button
 * }
 *
 * if (hasAnyPermission(['crm.change_sales_order', 'crm.view_sales_order'])) {
 *   // Show edit or view button
 * }
 */
// ── Implied permissions ──────────────────────────────────────────────────────
// **Served from the Python definition since Phase 5** — `services/permissionRules.js`
// fetches `GET /users/api/permission-rules/`, so this file is no longer a second
// source of truth for the rule. The map below is the OFFLINE FALLBACK, used
// before the fetch resolves and whenever it fails: a failed request must not
// remove a sidebar entry the API would allow.
//
// Keep it in step anyway. It is what runs on the first render of every page
// load, so a stale copy here is a flicker of the wrong menu, not nothing.
//
// `workflow.view_approvals` is the case this exists for. Approvals raised by
// every module land in ONE inbox at /workflow/my-approvals, so a user granted
// `srm.approve_purchase_order` but not `workflow.view_approvals` can approve
// nothing — the permission they hold has no reachable screen. It is therefore
// derived from the approval permissions they already have, rather than needing
// a separate grant on every approver role.
//
// Visibility only: the workflow API scopes an approver to their OWN assigned
// items and refuses an action by anyone but the assignee.
const APPROVAL_PERMISSION_MARKERS = ['.approve_', '.release_', '.post_', '.approve'];

const FALLBACK_IMPLIED_PERMISSIONS = {
  'workflow.view_approvals': {
    rule: 'any_codename_contains',
    markers: APPROVAL_PERMISSION_MARKERS,
  },
};

/**
 * Whether `permissions` satisfies `codename` by implication.
 *
 * Evaluates the served rule where there is one, else the fallback above. An
 * unrecognised `rule` returns false rather than guessing — a rule this client
 * cannot evaluate must never be treated as satisfied.
 */
const satisfiesImplied = (codename, permissions) => {
  const served = getImpliedRules();
  const spec = (served && served[codename]) || FALLBACK_IMPLIED_PERMISSIONS[codename];
  if (!spec || spec.rule !== 'any_codename_contains') return false;
  return (permissions || []).some(
    (held) => (spec.markers || []).some((marker) => held.includes(marker)),
  );
};

// ── Feature licensing ────────────────────────────────────────────────────────
// `licensedPrefixes` is null when every module is licensed (enforcement off or
// report-only); otherwise the RBAC codename prefixes the tenant has bought
// (`apps/licensing/catalog.py`). A codename outside it is UNLICENSED — for
// tenant admins too, which is why it is checked before the admin shortcut.
// Visibility only: the API refuses an unlicensed module with 403
// `feature_not_licensed` either way.
export const isCodenameLicensed = (codename, licensedPrefixes) => {
  if (!licensedPrefixes || !codename || typeof codename !== 'string') return true;
  const dot = codename.indexOf('.');
  if (dot <= 0) return true;
  return licensedPrefixes.includes(codename.slice(0, dot));
};

const usePermissions = () => {
  const { permissions, groups, isTenantAdmin, licensedPrefixes } = useSelector((state) => state.auth);

  /**
   * Whether the tenant's licence covers the module a codename belongs to.
   * @param {string} codename - e.g. 'rem.view_property'
   * @returns {boolean}
   */
  const isLicensed = (codename) => isCodenameLicensed(codename, licensedPrefixes);

  /**
   * Check if user has a specific permission — directly, or implied by another
   * permission they hold (see IMPLIED_PERMISSIONS above).
   * @param {string} permission - Permission codename (e.g., 'crm.add_sales_order')
   * @returns {boolean} True if user has the permission
   */
  const hasPermission = (permission) => {
    if (!permission) return false;

    // An unlicensed module is closed to everyone, tenant admins included.
    if (!isLicensed(permission)) return false;

    // Tenant admins have all permissions
    if (isTenantAdmin) return true;

    if (permissions?.includes(permission)) return true;

    return satisfiesImplied(permission, permissions);
  };

  /**
   * Check if user has ANY of the specified permissions
   * @param {Array<string>} permissionList - Array of permission codenames
   * @returns {boolean} True if user has at least one of the permissions
   */
  const hasAnyPermission = (permissionList) => {
    if (!Array.isArray(permissionList) || permissionList.length === 0) return false;

    return permissionList.some((permission) => hasPermission(permission));
  };

  /**
   * Check if user has ALL of the specified permissions
   * @param {Array<string>} permissionList - Array of permission codenames
   * @returns {boolean} True if user has all of the permissions
   */
  const hasAllPermissions = (permissionList) => {
    if (!Array.isArray(permissionList) || permissionList.length === 0) return false;

    return permissionList.every((permission) => hasPermission(permission));
  };

  /**
   * Check if user belongs to a specific group (role)
   * @param {string} groupName - Role/group name (e.g., 'Sales Manager')
   * @returns {boolean} True if user belongs to the group
   */
  const hasGroup = (groupName) => {
    if (!groupName) return false;

    return groups?.includes(groupName) || false;
  };

  /**
   * Check if user belongs to ANY of the specified groups
   * @param {Array<string>} groupList - Array of role/group names
   * @returns {boolean} True if user belongs to at least one group
   */
  const hasAnyGroup = (groupList) => {
    if (!Array.isArray(groupList) || groupList.length === 0) return false;

    return groupList.some((groupName) => groups?.includes(groupName));
  };

  /**
   * Check if user belongs to ALL of the specified groups
   * @param {Array<string>} groupList - Array of role/group names
   * @returns {boolean} True if user belongs to all groups
   */
  const hasAllGroups = (groupList) => {
    if (!Array.isArray(groupList) || groupList.length === 0) return false;

    return groupList.every((groupName) => groups?.includes(groupName));
  };

  /**
   * Get all user permissions
   * @returns {Array<string>} Array of permission codenames
   */
  const getPermissions = () => {
    return permissions || [];
  };

  /**
   * Get all user groups (roles)
   * @returns {Array<string>} Array of group names
   */
  const getGroups = () => {
    return groups || [];
  };

  /**
   * Check if user is tenant admin
   * @returns {boolean} True if user is tenant admin
   */
  const isAdmin = () => {
    return isTenantAdmin || false;
  };

  return {
    // Permission checking
    hasPermission,
    isLicensed,
    licensedPrefixes,
    hasAnyPermission,
    hasAllPermissions,

    // Group checking
    hasGroup,
    hasAnyGroup,
    hasAllGroups,

    // Data accessors
    getPermissions,
    getGroups,

    // Admin check
    isTenantAdmin: isAdmin(),
    isAdmin,

    // Raw data (for advanced use cases)
    permissions,
    groups,
  };
};

export { usePermissions };
export default usePermissions;
